<template>
  <main>
    <loading
      :active.sync="loading"
      color="#556ee6"
      loader="dots"
      :can-cancel="false"
      :is-full-page="false"
    />

    <!-- Step de Identificação (se habilitado) -->
    <IdentificationStep
      v-if="identificationEnabled && currentStep === 0"
      :field-type="identificationField"
      :submitted="submitted"
      @submit="handleIdentification"
    />

    <!-- Multi-step Form -->
    <section v-if="showForm && currentStep > 0" class="flex h-full flex-col">
      <!-- Barra de Progresso -->
      <StepNavigation
        v-if="isWizard && showProgress"
        :steps="visibleSteps"
        :current-step="wizardStep"
        :total-steps="totalWizardSteps"
      />

      <!-- Steps do Formulário -->
      <form @submit.prevent="handleSubmit">
        <DynamicStep
          v-for="(step, index) in visibleSteps"
          :key="step.id"
          v-show="wizardStep === index + 1"
          :step="step"
          :form-data="formData"
          :all-fields="allFields"
          :submitted="submitted"
          @update:field="updateField"
        />

        <!-- Step de Reconhecimento Facial (se habilitado) -->
        <FacialRecognition
          v-if="facialEnabled && wizardStep === facialStepIndex"
          v-show="wizardStep === facialStepIndex"
          :submitted="submitted"
          @capture="handleFacialCapture"
        />

        <!-- Erro da API -->
        <div v-if="apiError" class="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-md mt-4 text-sm">
          {{ apiError }}
        </div>

        <!-- Navegação -->
        <div class="flex justify-between mt-8 gap-4">
          <button
            v-if="wizardStep > 1"
            type="button"
            @click="prevStep"
            class="bg-gray-500 w-full text-white px-4 py-2 rounded-md hover:bg-gray-600"
          >
            Anterior
          </button>
          <button
            type="button"
            @click="nextStep"
            :disabled="isSubmitting"
            class="button-subs w-full text-white px-4 py-2 rounded-md transition-all disabled:opacity-50"
          >
            {{ wizardStep === totalWizardSteps ? submitButtonText : 'Próximo' }}
          </button>
        </div>
      </form>
    </section>

    <!-- Tela de Sucesso -->
    <SuccessScreen
      v-if="showThanks"
      :title="successScreen.title"
      :message="successScreen.message"
      :icon="successScreen.icon"
      :participant="participant"
    />
  </main>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { useFormSchema } from '@/composables/useFormSchema';
import { useFormAutoSave } from '@/composables/useFormAutoSave';
import { buildValidationRules, debounce } from '@/utils/validationBuilder';
import { shouldShowField, shouldShowStep, getRequiredVisibleFields } from '@/utils/conditionEvaluator';
import { executeBehaviors } from '@/utils/behaviorEngine';
import { submitForm, identifyParticipant } from '@/services/api/forms';
import useVuelidate from '@vuelidate/core';
import Loading from 'vue-loading-overlay';
import IdentificationStep from './IdentificationStep.vue';
import StepNavigation from './StepNavigation.vue';
import DynamicStep from './DynamicStep.vue';
import FacialRecognition from './FacialRecognition.vue';
import SuccessScreen from './SuccessScreen.vue';

export default {
  name: 'DynamicForm',
  components: {
    Loading,
    IdentificationStep,
    StepNavigation,
    DynamicStep,
    FacialRecognition,
    SuccessScreen
  },
  props: {
    slug: {
      type: String,
      required: true
    }
  },
  setup(props) {
    // Estado
    const loading = ref(false);
    const submitted = ref(false);
    const isSubmitting = ref(false);
    const showForm = ref(false);
    const showThanks = ref(false);
    const currentStep = ref(0);
    const wizardStep = ref(1);
    const formData = ref({});
    const facialBlob = ref(null);
    const participant = ref(null);
    const apiError = ref(null);

    // Carrega schema
    const {
      schema,
      isWizard,
      steps,
      fields,
      settings,
      facialEnabled,
      identificationEnabled,
      identificationField,
      successScreen,
      submitText,
      showProgress,
      autoSave,
      fetchSchema
    } = useFormSchema(props.slug);

    // Auto-save (habilitado se configurado no backend)
    const { hasSavedData, clear: clearSavedData } = useFormAutoSave(
      props.slug,
      formData,
      {
        enabled: computed(() => autoSave.value),
        debounceDelay: 1000
      }
    );

    // Todos os campos (ANTES de filtrar steps visíveis - necessário para avaliar condições)
    const allFields = computed(() => {
      const fieldsFromSteps = steps.value.flatMap(step => step.fields || []);
      const standaloneFields = fields.value || [];
      return [...fieldsFromSteps, ...standaloneFields];
    });

    // Steps visíveis (baseado em condições)
    const visibleSteps = computed(() => {
      return steps.value.filter(step => shouldShowStep(step, formData.value, allFields.value));
    });

    // Steps totais (incluindo facial se habilitado)
    const totalWizardSteps = computed(() => {
      let total = visibleSteps.value.length;
      if (facialEnabled.value) total++;
      return total;
    });

    // Índice do step facial
    const facialStepIndex = computed(() => totalWizardSteps.value);

    // Texto do botão de submit
    const submitButtonText = computed(() => submitText.value || 'Enviar');

    // Regras de validação dinâmicas
    const validationRules = computed(() => buildValidationRules(allFields.value));
    const v$ = useVuelidate(validationRules, formData);

    // Validação em tempo real (debounced)
    const debouncedValidation = debounce(() => {
      if (submitted.value) {
        v$.value.$touch();
      }
    }, 300);

    // Watch formData para validação em tempo real
    watch(
      () => formData.value,
      () => {
        debouncedValidation();
      },
      { deep: true }
    );

    // Carrega formulário na montagem
    onMounted(async () => {
      loading.value = true;
      try {
        await fetchSchema();

        // Verifica se há dados salvos
        if (hasSavedData()) {
          console.log('Dados salvos encontrados - foram restaurados automaticamente');
        }

        if (!identificationEnabled.value) {
          showForm.value = true;
          currentStep.value = 1;
        }
      } catch (err) {
        console.error('Erro ao carregar formulário:', err);
      } finally {
        loading.value = false;
      }
    });

    // Identificação inicial
    const handleIdentification = async (value) => {
      loading.value = true;
      try {
        const resp = await identifyParticipant(props.slug, {
          [identificationField.value]: value
        });

        if (resp.ok) {
          participant.value = resp.data;
          // Preenche dados existentes
          Object.assign(formData.value, resp.data);
          showForm.value = true;
          currentStep.value = 1;
        }
      } catch (err) {
        console.error('Erro na identificação:', err);
        // Mesmo que não encontre, permite continuar
        showForm.value = true;
        currentStep.value = 1;
      } finally {
        loading.value = false;
      }
    };

    // Atualiza campo + executa behaviors
    const updateField = (fieldName, value) => {
      formData.value[fieldName] = value;

      const field = allFields.value.find(f => f.name === fieldName);
      if (field?.behaviors) {
        // Prioriza campos do step atual para behaviors
        let fieldsForBehavior = allFields.value;
        if (visibleSteps.value && visibleSteps.value.length > 0 && wizardStep.value > 0) {
          const currentStepData = visibleSteps.value[wizardStep.value - 1];
          if (currentStepData?.fields?.length > 0) {
            fieldsForBehavior = currentStepData.fields;
          }
        }
        executeBehaviors(field, value, fieldsForBehavior, (name, val) => {
          formData.value[name] = val;
        });
      }
    };

    // Validação por step
    const validateCurrentStep = () => {
      submitted.value = true;

      // Step facial não tem campos para validar
      if (wizardStep.value === facialStepIndex.value) {
        return !!facialBlob.value;
      }

      const currentStepData = visibleSteps.value[wizardStep.value - 1];
      if (!currentStepData) return true;

      const currentStepFields = currentStepData.fields || [];
      const visibleFields = currentStepFields.filter(f => shouldShowField(f, formData.value, allFields.value));
      const requiredFields = getRequiredVisibleFields(currentStepFields, formData.value, allFields.value);

      // Toca todos os campos visíveis
      visibleFields.forEach(field => {
        if (v$.value[field.name]) {
          v$.value[field.name].$touch();
        }
      });

      // Verifica se há erros nos campos visíveis (obrigatórios + formato inválido)
      const hasErrors = visibleFields.some(field => {
        return v$.value[field.name]?.$error;
      });

      return !hasErrors;
    };

    // Próximo step
    const nextStep = () => {
      if (!validateCurrentStep()) {
        console.warn('Validação falhou no step atual');
        return;
      }

      if (wizardStep.value === totalWizardSteps.value) {
        handleSubmit();
      } else {
        wizardStep.value++;
        submitted.value = false; // Reseta submitted para o próximo step
      }
    };

    // Step anterior
    const prevStep = () => {
      if (wizardStep.value > 1) {
        wizardStep.value--;
        submitted.value = false;
      }
    };

    // Captura facial
    const handleFacialCapture = (blob) => {
      facialBlob.value = blob;
    };

    // Submit final
    const handleSubmit = async () => {
      if (isSubmitting.value || loading.value) return; // Guard clause
      loading.value = true;
      isSubmitting.value = true;
      submitted.value = true;
      apiError.value = null;

      try {
        const formDataToSend = new FormData();

        // Campos do formulário
        Object.keys(formData.value).forEach(key => {
          const value = formData.value[key];
          if (value instanceof File) {
            formDataToSend.append(key, value);
          } else if (value !== null && value !== undefined && value !== '') {
            formDataToSend.append(key, value);
          }
        });

        // Selfie (se facial habilitado)
        if (facialBlob.value) {
          formDataToSend.append('selfie', facialBlob.value, 'selfie.jpg');
        }

        // ID do participante (se identificação habilitada)
        if (participant.value?.id) {
          formDataToSend.append('id', participant.value.id);
        }

        // LGPD
        formDataToSend.append('authorizes_data_usage', 1);

        const resp = await submitForm(props.slug, formDataToSend);
        participant.value = resp.data || resp;

        // Limpa dados salvos após sucesso
        clearSavedData();

        showForm.value = false;
        showThanks.value = true;
      } catch (err) {
        console.error('Erro ao enviar formulário:', err);
        const data = err.response?.data;
        if (data?.errors) {
          const msgs = Object.values(data.errors).flat();
          apiError.value = msgs.join('. ');
        } else if (data?.message) {
          apiError.value = data.message;
        } else {
          apiError.value = 'Ocorreu um erro ao enviar o formulário. Tente novamente.';
        }
      } finally {
        loading.value = false;
        isSubmitting.value = false;
      }
    };

    return {
      loading,
      submitted,
      isSubmitting,
      showForm,
      showThanks,
      currentStep,
      wizardStep,
      formData,
      participant,
      apiError,
      isWizard,
      visibleSteps,
      facialEnabled,
      identificationEnabled,
      identificationField,
      successScreen,
      totalWizardSteps,
      facialStepIndex,
      submitButtonText,
      showProgress,
      handleIdentification,
      updateField,
      nextStep,
      prevStep,
      handleFacialCapture,
      handleSubmit
    };
  }
};
</script>

<style scoped>
.button-subs {
  background-color: #556ee6;
}

.button-subs:hover {
  background-color: #4559c9;
}
</style>
